import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
  Switch,
  FormControlLabel,
  Checkbox,
  Toolbar,
  Autocomplete,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  ReceiptLong as ReceiptLongIcon,
  Close as CloseIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import jobWorksApi from '../api/jobWorks';
import InvoiceDialog from '../components/jobwork/InvoiceDialog';
import { generatePendingJobWorksReport, PendingJobWorkItem } from '../utils/pdfGenerator';
import { format, parseISO } from 'date-fns';
import { Theme } from '@mui/material/styles';

const JobWorksPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobWorks, setJobWorks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('jobWorks_searchTerm') || '');
  const [success, setSuccess] = useState<string | null>(null);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [showCompleted, setShowCompleted] = useState(() => localStorage.getItem('jobWorks_showCompleted') === 'true');
  const [vendorFilter, setVendorFilter] = useState(searchParams.get('vendor') || localStorage.getItem('jobWorks_vendorFilter') || '');
  const [jobTypeFilter, setJobTypeFilter] = useState(searchParams.get('jobType') || localStorage.getItem('jobWorks_jobTypeFilter') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || localStorage.getItem('jobWorks_statusFilter') || '');
  const [startDate, setStartDate] = useState<string>(() => localStorage.getItem('jobWorks_startDate') || '');
  const [endDate, setEndDate] = useState<string>(() => localStorage.getItem('jobWorks_endDate') || '');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceJobWork, setInvoiceJobWork] = useState<any>(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);

  // Persist filters to localStorage
  useEffect(() => { localStorage.setItem('jobWorks_searchTerm', searchTerm); }, [searchTerm]);
  useEffect(() => { localStorage.setItem('jobWorks_showCompleted', String(showCompleted)); }, [showCompleted]);
  useEffect(() => { localStorage.setItem('jobWorks_vendorFilter', vendorFilter); }, [vendorFilter]);
  useEffect(() => { localStorage.setItem('jobWorks_jobTypeFilter', jobTypeFilter); }, [jobTypeFilter]);
  useEffect(() => { localStorage.setItem('jobWorks_statusFilter', statusFilter); }, [statusFilter]);
  useEffect(() => { localStorage.setItem('jobWorks_startDate', startDate); }, [startDate]);
  useEffect(() => { localStorage.setItem('jobWorks_endDate', endDate); }, [endDate]);

  const handleOpenInvoiceDialog = (jobWork: any) => {
    setInvoiceJobWork(jobWork);
    setInvoiceDialogOpen(true);
  };

  const handleCloseInvoiceDialog = () => {
    setInvoiceDialogOpen(false);
    setInvoiceJobWork(null);
  };

  const handleInvoiceSaved = async () => {
    try {
      const data = await jobWorksApi.getAll();
      setJobWorks(data);
      setSuccess('Invoice saved successfully');
    } catch (err) {
      console.error('Error refreshing job works:', err);
    }
  };

  // Fetch job works (includes all vendors including internal)
  useEffect(() => {
    const fetchJobWorks = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await jobWorksApi.getAll();
        // Include all job works including internal vendors
        setJobWorks(data);
      } catch (err) {
        console.error('Error fetching job works:', err);
        setError('Failed to load job works. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobWorks();
  }, []);

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Options for dropdown filters
  const vendorOptions = React.useMemo(() => {
    const set = new Set<string>();
    jobWorks.forEach(j => {
      if (j.vendor?.registered_name) set.add(j.vendor.registered_name);
      if (j.vendor?.alias) set.add(j.vendor.alias);
    });
    return Array.from(set).sort();
  }, [jobWorks]);

  const jobTypeOptions = React.useMemo(() => {
    const set = new Set<string>();
    jobWorks.forEach(j => {
      const jt = j.jobType && typeof j.jobType === 'object' && j.jobType !== null ? j.jobType.name : j.jobType;
      if (jt) set.add(jt);
    });
    return Array.from(set).sort();
  }, [jobWorks]);

  const statusOptions = React.useMemo(() => {
    const set = new Set<string>();
    jobWorks.forEach(j => { if (j.status) set.add(j.status); });
    return Array.from(set).sort();
  }, [jobWorks]);

  // Filter job works based on search term and selected filters
  const filteredJobWorks = jobWorks.filter(jobWork => {
    const matchesSearch =
      jobWork.jobWorkCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      jobWork.designNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (jobWork.vendor && jobWork.vendor.registered_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const isActive = ['PENDING', 'ISSUED', 'PROCESSING', 'PARTIALLY_RECEIVED'].includes(jobWork.status);
    const vendorMatch = !vendorFilter || (jobWork.vendor && (
      jobWork.vendor.registered_name === vendorFilter || 
      jobWork.vendor.alias === vendorFilter
    ));
    // Filter out legacy INTERNAL-type vendors (internal departments) unless explicitly selected.
    // JOB_WORK_VENDOR type vendors are real job-work parties and must always be shown.
    const isLegacyInternal = jobWork.vendor &&
      (jobWork.vendor.vendor_type === 'INTERNAL' ||
        (!jobWork.vendor.vendor_type && jobWork.vendor.is_internal &&
          jobWork.vendor.registered_name.toLowerCase().includes('inhouse stitching')));

    if (isLegacyInternal && vendorFilter !== jobWork.vendor?.registered_name) {
      return false;
    }

    const jt = jobWork.jobType && typeof jobWork.jobType === 'object' && jobWork.jobType !== null ? jobWork.jobType.name : jobWork.jobType;
    const jobTypeMatch = !jobTypeFilter || jt === jobTypeFilter;
    // Special pseudo-filter: "NOT_INVOICED" = received but no invoice
    const statusMatch = !statusFilter 
      || (statusFilter === 'NOT_INVOICED' 
        ? (['PARTIALLY_RECEIVED', 'COMPLETED'].includes(jobWork.status) && !jobWork.invoiceNumber)
        : jobWork.status === statusFilter);
    const dateObj = jobWork.issueDate ? parseISO(jobWork.issueDate) : null;
    const startOk = !startDate || (dateObj && dateObj >= parseISO(startDate));
    const endOk = !endDate || (dateObj && dateObj <= parseISO(endDate));
    const dateMatch = startOk && endOk;
    const activeOrAllowed = showCompleted || isActive || statusFilter === 'NOT_INVOICED';
    return matchesSearch && activeOrAllowed && vendorMatch && jobTypeMatch && statusMatch && dateMatch;
  });

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
        return 'error';
      case 'PROCESSING':
        return 'info';
      case 'PARTIALLY_RECEIVED':
        return 'warning';
      default:
        return 'primary';
    }
  };

  // Navigate to job work details
  const handleViewDetails = (id: number) => {
    navigate(`/job-works/${id}`);
  };

  // Navigate to create new job work
  const handleCreateNew = () => {
    navigate('/job-works/new');
  };

  // Handle cancel job work
  const handleCancel = async (id: number) => {
    if (window.confirm('Are you sure you want to cancel this job work? This action cannot be undone.')) {
      try {
        setLoading(true);
        await jobWorksApi.cancel(id);
        // Refresh job works list
        const data = await jobWorksApi.getAll();
        setJobWorks(data);
        setSuccess('Job work cancelled successfully');
      } catch (err) {
        console.error('Error cancelling job work:', err);
        setError('Failed to cancel job work. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Selection helpers
  const cancellableOnPage = filteredJobWorks
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    .filter((jw: any) => jw.status !== 'COMPLETED' && jw.status !== 'CANCELLED');

  const allPageSelected = cancellableOnPage.length > 0 && cancellableOnPage.every((jw: any) => selectedIds.has(jw.id));
  const somePageSelected = cancellableOnPage.some((jw: any) => selectedIds.has(jw.id));

  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (allPageSelected) {
      // Deselect all on current page
      setSelectedIds(prev => {
        const next = new Set(prev);
        cancellableOnPage.forEach((jw: any) => next.delete(jw.id));
        return next;
      });
    } else {
      // Select all cancellable on current page
      setSelectedIds(prev => {
        const next = new Set(prev);
        cancellableOnPage.forEach((jw: any) => next.add(jw.id));
        return next;
      });
    }
  };

  const handleBulkCancel = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Are you sure you want to cancel ${selectedIds.size} job work(s)? This action cannot be undone.`)) return;
    try {
      setLoading(true);
      setError(null);
      const result = await jobWorksApi.bulkCancel(Array.from(selectedIds));
      setSuccess(result.message || `${result.cancelled} job work(s) cancelled successfully`);
      setSelectedIds(new Set());
      // Refresh list
      const data = await jobWorksApi.getAll();
      setJobWorks(data);
    } catch (err: any) {
      console.error('Error bulk cancelling job works:', err);
      setError(err?.response?.data?.error || 'Failed to bulk cancel job works. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get design image URL (if available)
  const getDesignImageUrl = (jobWork: any) => {
    if (jobWork.design && jobWork.design.image_url) return jobWork.design.image_url;
    return null;
  };

  // Calculate pending to receive quantity
  const getPendingToReceive = (jobWork: any) => {
    if (jobWork.processMaterial) {
      // Material-based: total issued material qty - total received material qty
      const totalIssued = (jobWork.materials || []).reduce((sum: number, m: any) => sum + (Number(m.quantity) || 0), 0);
      const totalReceived = (jobWork.receivedItems || []).reduce((sum: number, r: any) => sum + (Number(r.materialQuantity) || 0), 0);
      const pending = totalIssued - totalReceived;
      return { pending: Math.max(0, pending), unit: 'mtr' };
    } else {
      // Piece-based: total design detail pieces - total received pieces
      const totalIssued = (jobWork.designDetails || []).reduce((sum: number, d: any) => sum + (Number(d.pieces) || 0), 0);
      const totalReceived = (jobWork.receivedItems || []).reduce((sum: number, r: any) => sum + (Number(r.pieceCount) || 0), 0);
      const pending = totalIssued - totalReceived;
      return { pending: Math.max(0, pending), unit: 'pcs' };
    }
  };

  // Navigate to designs page with design number filter and open preview
  const handleDesignNoClick = (designNo: string) => {
    localStorage.setItem('designsPage_searchQuery', designNo);
    navigate(`/designs?search=${encodeURIComponent(designNo)}&preview=true`);
  };

  // Open image preview popup
  const handleImageClick = (imageUrl: string) => {
    setImagePreviewUrl(imageUrl);
    setImagePreviewOpen(true);
  };

  // Generate pending job works report PDF for selected vendor
  const handlePrintPendingReport = async () => {
    if (!vendorFilter) return;

    // Get all pending job works for this vendor (not just current page)
    const pendingForVendor = jobWorks.filter(jw => {
      const vendorMatch = jw.vendor && (
        jw.vendor.registered_name === vendorFilter || 
        jw.vendor.alias === vendorFilter
      );
      const isPending = ['PENDING', 'ISSUED', 'PROCESSING', 'PARTIALLY_RECEIVED'].includes(jw.status);
      return vendorMatch && isPending;
    });

    if (pendingForVendor.length === 0) {
      setError('No pending job works found for this vendor.');
      return;
    }

    try {
      setGeneratingReport(true);
      const pendingItems: PendingJobWorkItem[] = pendingForVendor.map(jw => {
        const { pending, unit } = getPendingToReceive(jw);
        const jt = jw.jobType && typeof jw.jobType === 'object' && jw.jobType !== null ? jw.jobType.name : jw.jobType;
        return {
          jobWorkCode: jw.jobWorkCode,
          designNo: jw.designNo,
          issueDate: jw.issueDate,
          pendingQty: pending,
          unit,
          imageUrl: getDesignImageUrl(jw),
          jobType: jt || '-',
          status: jw.status
        };
      });

      await generatePendingJobWorksReport(vendorFilter, pendingItems);
      setSuccess('Pending report downloaded successfully');
    } catch (err) {
      console.error('Error generating pending report:', err);
      setError('Failed to generate pending report. Please try again.');
    } finally {
      setGeneratingReport(false);
    }
  };

  return (
    <Box sx={{ p: isMobile ? 0 : 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Job Works
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {vendorFilter && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={generatingReport ? <CircularProgress size={16} /> : <PrintIcon />}
              onClick={handlePrintPendingReport}
              disabled={generatingReport}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {generatingReport ? 'Generating...' : 'Print Pending Report'}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create New
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3, px: isMobile ? 0 : 2, boxShadow: 0, borderRadius: 0 }}>
        <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.5fr 1fr 1fr 1fr 1fr 1fr', gap: 2, alignItems: 'center' }}>
          <TextField
            variant="outlined"
            placeholder="Search by job work code, design number, or vendor"
            value={searchTerm}
            onChange={handleSearch}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
          />
          <Autocomplete
            size="small"
            options={vendorOptions}
            value={vendorFilter || null}
            onChange={(_e, newValue) => {
              setVendorFilter(newValue || '');
              if (searchParams.has('vendor')) {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('vendor');
                setSearchParams(newParams, { replace: true });
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Vendor" placeholder="Search vendor..." />
            )}
            clearOnEscape
            openOnFocus
          />
          <FormControl size="small">
            <InputLabel id="jobtype-filter-label">Job Type</InputLabel>
            <Select labelId="jobtype-filter-label" label="Job Type" value={jobTypeFilter} onChange={(e) => setJobTypeFilter(e.target.value)}>
              <MenuItem value=""><em>All</em></MenuItem>
              {jobTypeOptions.map(j => (
                <MenuItem key={j} value={j}>{j}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select labelId="status-filter-label" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value=""><em>All</em></MenuItem>
              <MenuItem value="NOT_INVOICED" sx={{ color: 'warning.main', fontWeight: 600 }}>
                Received (Not Invoiced)
              </MenuItem>
              {statusOptions.map(s => (
                <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Start Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <TextField label="End Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <FormControlLabel
            control={<Switch checked={showCompleted} onChange={(_e, checked) => setShowCompleted(checked)} color="primary" />}
            label="Show Completed/Cancelled"
            sx={{ ml: 0 }}
          />
          {(searchTerm || vendorFilter || jobTypeFilter || statusFilter || startDate || endDate || showCompleted) && (
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => {
                setSearchTerm('');
                setVendorFilter('');
                setJobTypeFilter('');
                setStatusFilter('');
                setStartDate('');
                setEndDate('');
                setShowCompleted(false);
                // Clear URL params too
                setSearchParams({}, { replace: true });
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Clear Filters
            </Button>
          )}
        </Box>
        {searchParams.get('vendor') && (
          <Box sx={{ px: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`Filtered by vendor: ${searchParams.get('vendor')}`}
              color="primary"
              onDelete={() => {
                setVendorFilter('');
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('vendor');
                setSearchParams(newParams, { replace: true });
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <Toolbar
          sx={{
            mb: 1,
            borderRadius: 1,
            bgcolor: 'error.light',
            color: 'error.contrastText',
            display: 'flex',
            justifyContent: 'space-between',
            minHeight: '48px !important',
            px: 2
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {selectedIds.size} selected
          </Typography>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<CancelIcon />}
            onClick={handleBulkCancel}
          >
            Cancel Selected
          </Button>
        </Toolbar>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ px: 0, mx: 0 }}>
                {filteredJobWorks
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((jobWork) => (
                    <Card key={jobWork.id} sx={{ mb: 2, borderRadius: 0, boxShadow: 1, position: 'relative', mx: 0 }}>
                      {/* Checkbox for cancellable items */}
                      {jobWork.status !== 'COMPLETED' && jobWork.status !== 'CANCELLED' && (
                        <Checkbox
                          checked={selectedIds.has(jobWork.id)}
                          onChange={() => handleToggleSelect(jobWork.id)}
                          sx={{ position: 'absolute', top: 0, left: 0, zIndex: 3 }}
                          size="small"
                        />
                      )}
                      {/* Job Type tag at top right */}
                      <Chip
                        label={jobWork.jobType && typeof jobWork.jobType === 'object' && jobWork.jobType !== null ? jobWork.jobType.name : jobWork.jobType || 'N/A'}
                        color="default"
                        size="small"
                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, bgcolor: '#e0e0e0', color: '#333', fontWeight: 600 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
                        {/* Left column: Design image on top, date at bottom */}
                        <Box sx={{ minWidth: 90, maxWidth: 90, bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', py: 1, height: 180 }}>
                          {/* Design image thumbnail (tall format) */}
                          <Box 
                            sx={{ width: 70, height: 120, borderRadius: 1, overflow: 'hidden', bgcolor: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, cursor: getDesignImageUrl(jobWork) ? 'pointer' : 'default' }}
                            onClick={() => { const url = getDesignImageUrl(jobWork); if (url) handleImageClick(url); }}
                          >
                            {getDesignImageUrl(jobWork) ? (
                              <img
                                src={getDesignImageUrl(jobWork)}
                                alt={jobWork.designNo}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                            ) : (
                              <Typography variant="caption" color="textSecondary">No Image</Typography>
                            )}
                          </Box>
                          {/* Date at the bottom */}
                          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 'auto' }}>
                            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, fontSize: 18 }}>
                              {jobWork.issueDate ? format(parseISO(jobWork.issueDate), 'MMM dd') : '--'}
                            </Typography>
                          </Box>
                        </Box>
                        {/* Right column: Details */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ pb: 1, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                                Job Work Code: {jobWork.jobWorkCode}
                              </Typography>
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                Design No:{' '}
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600, ml: 0.5, '&:hover': { textDecoration: 'underline' } }}
                                  onClick={() => handleDesignNoClick(jobWork.designNo)}
                                >
                                  {jobWork.designNo}
                                </Typography>
                              </Typography>
                              <Typography variant="body2" component="div">
                                Vendor: {jobWork.vendor ? jobWork.vendor.registered_name : 'N/A'}
                                {jobWork.vendor && (
                                  <Typography variant="caption" component="span" sx={{ ml: 0.5, color: jobWork.vendor.vendor_type === 'INTERNAL' ? 'success.main' : 'text.secondary', fontWeight: 500 }}>
                                    ({jobWork.vendor.vendor_type === 'INTERNAL' ? 'Internal' : 'External'})
                                  </Typography>
                                )}
                              </Typography>
                              <Typography variant="body2">
                                Job Type: {jobWork.jobType ? (typeof jobWork.jobType === 'object' && jobWork.jobType !== null ? jobWork.jobType.name : jobWork.jobType) : 'N/A'}
                              </Typography>
                              <Typography variant="body2">
                                Expected Return: {jobWork.expectedReturn ? format(parseISO(jobWork.expectedReturn), 'dd/MM/yyyy') : 'N/A'}
                              </Typography>
                              {/* Pending to receive */}
                              {jobWork.status !== 'CANCELLED' && (() => {
                                const { pending, unit } = getPendingToReceive(jobWork);
                                return pending > 0 ? (
                                  <Typography variant="body2" color="warning.main" fontWeight={600}>
                                    Pending: {pending} {unit}
                                  </Typography>
                                ) : null;
                              })()}
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                Status: <Chip label={jobWork.status.replace('_', ' ')} color={getStatusColor(jobWork.status)} size="small" />
                                {jobWork.status === 'COMPLETED' && !jobWork.invoiceNumber && (
                                  <Chip 
                                    label="NOT INVOICED" 
                                    color="warning"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Typography>
                            </Box>
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'flex-end', pr: 2, pb: 1 }}>
                            <IconButton color="primary" onClick={() => handleViewDetails(jobWork.id)} title="View Details">
                              <VisibilityIcon />
                            </IconButton>
                            {/* Add Invoice button for received but not invoiced items */}
                            {['PARTIALLY_RECEIVED', 'COMPLETED'].includes(jobWork.status) && !jobWork.invoiceNumber && (
                              <IconButton color="warning" onClick={() => handleOpenInvoiceDialog(jobWork)} title="Add Invoice">
                                <ReceiptLongIcon />
                              </IconButton>
                            )}
                            {jobWork.status !== 'COMPLETED' && jobWork.status !== 'CANCELLED' && (
                              <>
                                <IconButton color="secondary" onClick={() => navigate(`/job-works/${jobWork.id}/edit`)} title="Edit">
                                  <EditIcon />
                                </IconButton>
                                <IconButton color="error" onClick={() => handleCancel(jobWork.id)} title="Cancel">
                                  <CancelIcon />
                                </IconButton>
                              </>
                            )}
                          </CardActions>
                        </Box>
                      </Box>
                    </Card>
                  ))}
                {filteredJobWorks.length === 0 && (
                  <Typography align="center" sx={{ mt: 2 }} color="textSecondary">
                    No job works found
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredJobWorks.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </Box>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={somePageSelected && !allPageSelected}
                            checked={allPageSelected}
                            onChange={handleToggleSelectAll}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>#</TableCell>
                        <TableCell>Image</TableCell>
                        <TableCell>Job Work Code</TableCell>
                        <TableCell>Design No</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Job Type</TableCell>
                        <TableCell>Issue Date</TableCell>
                        <TableCell>Expected Return</TableCell>
                        <TableCell>Pending</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredJobWorks
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((jobWork, idx) => (
                          <TableRow key={jobWork.id} selected={selectedIds.has(jobWork.id)}>
                            <TableCell padding="checkbox">
                              {jobWork.status !== 'COMPLETED' && jobWork.status !== 'CANCELLED' ? (
                                <Checkbox
                                  checked={selectedIds.has(jobWork.id)}
                                  onChange={() => handleToggleSelect(jobWork.id)}
                                  size="small"
                                />
                              ) : null}
                            </TableCell>
                            <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                            <TableCell>
                              {getDesignImageUrl(jobWork) ? (
                                <Box 
                                  sx={{ width: 56, height: 80, borderRadius: 1, overflow: 'hidden', bgcolor: '#eee', cursor: 'pointer' }}
                                  onClick={() => handleImageClick(getDesignImageUrl(jobWork)!)}
                                >
                                  <img src={getDesignImageUrl(jobWork)} alt={jobWork.designNo} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                </Box>
                              ) : (
                                <Typography variant="caption" color="textSecondary">No Image</Typography>
                              )}
                            </TableCell>
                            <TableCell>{jobWork.jobWorkCode}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                                onClick={() => handleDesignNoClick(jobWork.designNo)}
                              >
                                {jobWork.designNo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2">{jobWork.vendor ? jobWork.vendor.registered_name : 'N/A'}</Typography>
                                {jobWork.vendor && (
                                  <Typography variant="caption" sx={{ color: jobWork.vendor.vendor_type === 'INTERNAL' ? 'success.main' : 'text.secondary', fontWeight: 500 }}>
                                    {jobWork.vendor.vendor_type === 'INTERNAL' ? 'Internal' : 'External'}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {jobWork.jobType ? 
                                (typeof jobWork.jobType === 'object' && jobWork.jobType !== null ? 
                                  jobWork.jobType.name : 
                                  jobWork.jobType) : 
                                'N/A'}
                            </TableCell>
                            <TableCell>
                              {jobWork.issueDate ? format(parseISO(jobWork.issueDate), 'dd/MM/yyyy') : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {jobWork.expectedReturn ? format(parseISO(jobWork.expectedReturn), 'dd/MM/yyyy') : 'N/A'}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const { pending, unit } = getPendingToReceive(jobWork);
                                if (jobWork.status === 'CANCELLED') return <Typography variant="caption" color="text.disabled">—</Typography>;
                                if (pending === 0) return <Typography variant="caption" color="success.main" fontWeight={600}>Done</Typography>;
                                return (
                                  <Typography variant="body2" fontWeight={600} color={pending > 0 ? 'warning.main' : 'text.primary'}>
                                    {pending} {unit}
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip 
                                  label={jobWork.status.replace('_', ' ')} 
                                  color={getStatusColor(jobWork.status)}
                                  size="small"
                                />
                                {jobWork.status === 'COMPLETED' && !jobWork.invoiceNumber && (
                                  <Chip 
                                    label="NOT INVOICED" 
                                    color="warning"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <IconButton 
                                color="primary" 
                                onClick={() => handleViewDetails(jobWork.id)}
                                title="View Details"
                              >
                                <VisibilityIcon />
                              </IconButton>
                              {/* Add Invoice button for received but not invoiced items */}
                              {['PARTIALLY_RECEIVED', 'COMPLETED'].includes(jobWork.status) && !jobWork.invoiceNumber && (
                                <IconButton 
                                  color="warning" 
                                  onClick={() => handleOpenInvoiceDialog(jobWork)}
                                  title="Add Invoice"
                                >
                                  <ReceiptLongIcon />
                                </IconButton>
                              )}
                              {jobWork.status !== 'COMPLETED' && jobWork.status !== 'CANCELLED' && (
                                <>
                                  <IconButton 
                                    color="secondary" 
                                    onClick={() => navigate(`/job-works/${jobWork.id}/edit`)}
                                    title="Edit"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton 
                                    color="error" 
                                    onClick={() => handleCancel(jobWork.id)}
                                    title="Cancel"
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      {filteredJobWorks.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={12} align="center">
                            No job works found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredJobWorks.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </>
        )}
      </Paper>

      {/* Invoice Dialog */}
      {invoiceJobWork && (
        <InvoiceDialog
          open={invoiceDialogOpen}
          onClose={handleCloseInvoiceDialog}
          jobWorkId={invoiceJobWork.id}
          jobWorkCode={invoiceJobWork.jobWorkCode}
          existingInvoice={invoiceJobWork.invoiceNumber ? {
            invoiceNumber: invoiceJobWork.invoiceNumber,
            invoiceDate: invoiceJobWork.invoiceDate,
            invoiceAmount: invoiceJobWork.invoiceAmount,
            taxAmount: invoiceJobWork.taxAmount,
            invoiceImageUrl: invoiceJobWork.invoiceImageUrl
          } : undefined}
          onSave={handleInvoiceSaved}
        />
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 1, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#000' }}>
          <IconButton
            onClick={() => setImagePreviewOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
          >
            <CloseIcon />
          </IconButton>
          {imagePreviewUrl && (
            <Box
              component="img"
              src={imagePreviewUrl}
              alt="Design Preview"
              sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default JobWorksPage; 